import React, { FC, useEffect, useState } from 'react';
import { Button, Card, Col, Dropdown, Input, Modal, Popover, Row, Skeleton, message } from 'antd';
import { CaretDownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import AIIcon from '../AIIcon';
import { Link } from 'react-router-dom';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import {
	ConfidenceLabel,
	Recommendation,
	RepairReplaceScore,
	UrgencySignal,
} from '../../types/asset_replacement_score';
import { assetRepairReplaceScoreGet, assetsRestCrudThunks } from '../../thunks/assets_thunks';
import {
	CONFIDENCE_TEXT_COLOR,
	CostsTile,
	CtaLinkList,
	ReliabilityTile,
	UsefulLifeTile,
	confidenceDisplayLabel,
} from './DimensionTile';
import { ZoneProgressBar } from './ZoneProgressBar';
import { ActiveZone, zoneChipStyle } from './colours';
import { ctaLinksFromMissingInputs } from './cta';
import { DeepAnalysisModal } from '../deep_analysis_modal/DeepAnalysisModal';
import { ImproveConfidenceModal } from './ImproveConfidenceModal';
import { withUserType } from '../withUserType';

interface ReplacementIndexCardProps {
	asset: any;
	userType: string;
}

const ZONE_DISPLAY: Record<ActiveZone, string> = {
	repair: 'Low',
	monitor: 'Medium',
	replace: 'High',
};

const HEADER_INFO_COPY =
	"A weighted value (low, medium, high) based on an asset's repair costs, downtime, and remaining useful life. The higher the replacement index, the more you should consider replacing the asset than repairing it.";

// -- Zone dropdown chip ------------------------------------------------------

const ZoneChip: FC<{ zone: ActiveZone; className?: string }> = ({ zone, className }) => (
	<span
		className={`h5medium inline-flex items-center gap-1 rounded-3xl px-s py-2xs ${className || ''}`}
		style={zoneChipStyle(zone)}
	>
		{ZONE_DISPLAY[zone]}
	</span>
);

const ZoneDropdown: FC<{
	zone: ActiveZone;
	calculatedZone: ActiveZone;
	onSelect: (zone: ActiveZone) => void;
}> = ({ zone, calculatedZone, onSelect }) => {
	const overlay = (
		<div className="rounded-sm bg-white py-1 shadow-notifications-card">
			<div className="bodyregular px-3 py-1 text-character-primary">Change Replacement Index</div>
			{(['replace', 'monitor', 'repair'] as ActiveZone[]).map((value) => (
				<div
					key={value}
					role="button"
					tabIndex={0}
					onClick={() => onSelect(value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.key === ' ') onSelect(value);
					}}
					className="flex w-full cursor-pointer items-center gap-xs px-3 py-1 hover:bg-grey-3"
				>
					<ZoneChip zone={value} />
					{value === calculatedZone ? (
						<span className="footnoteregular text-right text-character-secondary">
							OpenWrench Calculated Index
						</span>
					) : null}
				</div>
			))}
		</div>
	);
	return (
		<Dropdown overlay={overlay} trigger={['click']}>
			<span className="inline-flex cursor-pointer items-center gap-1">
				<ZoneChip zone={zone} />
				<CaretDownOutlined style={{ fontSize: 10 }} />
			</span>
		</Dropdown>
	);
};

// -- Zone + urgency row ------------------------------------------------------

const recommendationMessage = (recommendation: Recommendation): string => {
	if (recommendation === 'replace') return 'Please plan to replace asset soon';
	if (recommendation === 'monitor') return 'Please plan to schedule replacement soon';
	return '';
};

const ZoneAndUrgencyRow: FC<{
	zone: ActiveZone;
	calculatedZone: ActiveZone;
	overrideZone: ActiveZone | null;
	overrideNote: string | null;
	urgency: UrgencySignal | null | undefined;
	recommendation: Recommendation;
	confidence: ConfidenceLabel;
	assetId: string | number;
	userType: string;
	onZoneSelect: (zone: ActiveZone) => void;
}> = ({
	zone,
	calculatedZone,
	overrideZone,
	overrideNote,
	urgency,
	recommendation,
	confidence,
	assetId,
	userType,
	onZoneSelect,
}) => {
	const isOverridden = !!overrideZone;
	const urgencyTotal = urgency ? urgency.emergencyCount + urgency.urgentCount : 0;
	const showUrgency = !!urgency && recommendation !== 'repair' && urgencyTotal > 0;
	const planMessage =
		!isOverridden && confidence === 'high' ? recommendationMessage(recommendation) : '';

	return (
		<div className="mt-2 flex flex-wrap items-center gap-xs">
			{isOverridden ? (
				<span
					className="h5medium inline-flex items-center gap-1 rounded-3xl bg-grey-4 px-s py-2xs text-grey-7 line-through"
					title="OpenWrench Calculated Index"
				>
					{ZONE_DISPLAY[calculatedZone]}
				</span>
			) : null}
			<ZoneDropdown zone={zone} calculatedZone={calculatedZone} onSelect={onZoneSelect} />
			{isOverridden ? (
				<span className="bodyregular text-character-primary">
					Replacement Index override:{' '}
					{overrideNote ? (
						<span className="italic text-character-secondary">&ldquo;{overrideNote}&rdquo;</span>
					) : null}
				</span>
			) : (
				<span className="bodyregular text-character-primary">Replacement Index</span>
			)}
			{showUrgency ? (
				<>
					<span className="text-character-secondary">·</span>
					<span className="bodyregular text-character-primary">
						<Link
							to={`/${userType}/workOrders/overview/active?assetId=${assetId}`}
							className="text-character-secondary underline hover:text-purple-7 hover:underline"
						>
							{urgencyTotal} emergency WO{urgencyTotal === 1 ? '' : 's'}{' '}
							<span className="ml-1">
								<i className="icons8-font icons8-external-link" />
							</span>
						</Link>{' '}
						in the last 90 days
					</span>
				</>
			) : null}
			{planMessage ? (
				<>
					<span className="text-character-secondary">·</span>
					<span className="bodyregular text-character-primary">{planMessage}</span>
				</>
			) : null}
		</div>
	);
};

// -- Change Replacement Index modal ------------------------------------------

const ZONE_CHIP_CLASS: Record<ActiveZone, string> = {
	repair: 'border-green-5 bg-green-1 text-green-700',
	monitor: 'border-orange-300 bg-orange-50 text-orange-600',
	replace: 'border-red-300 bg-red-50 text-red-600',
};

const ChangeReplacementIndexModal: FC<{
	open: boolean;
	initialZone: ActiveZone;
	assetId: string | number;
	userType: string;
	onCancel: () => void;
	onSaved: () => void;
}> = ({ open, initialZone, assetId, userType, onCancel, onSaved }) => {
	const dispatch = useDispatch();
	const [selected, setSelected] = useState<ActiveZone>(initialZone);
	const [reason, setReason] = useState('');
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			setSelected(initialZone);
			setReason('');
			setSaving(false);
		}
	}, [open, initialZone]);

	const trimmedReason = reason.trim();
	const okDisabled = trimmedReason.length === 0 || saving;

	const handleOk = () => {
		if (okDisabled) return;
		setSaving(true);
		(dispatch as any)(
			assetsRestCrudThunks(userType).updatePartially(assetId, {
				userOverrideReplacementRecommendation: selected,
				userOverrideReplacementRecommendationNotes: trimmedReason,
			})
		)
			.then(() => {
				setSaving(false);
				message.success('Replacement Index overridden.');
				onSaved();
				onCancel();
			})
			.catch((err: any) => {
				setSaving(false);
				message.error(typeof err === 'string' ? err : 'Failed to save override.');
			});
	};

	return (
		<Modal
			title="Change Replacement Index"
			visible={open}
			onCancel={onCancel}
			onOk={handleOk}
			okButtonProps={{ disabled: okDisabled, loading: saving }}
			destroyOnClose
		>
			<div className="flex flex-col gap-m">
				<div className="flex gap-xs">
					{(['replace', 'monitor', 'repair'] as ActiveZone[]).map((zone) => (
						<button
							key={zone}
							type="button"
							onClick={() => setSelected(zone)}
							className={`rounded-3xl border border-solid px-s py-xxs ${
								selected === zone ? ZONE_CHIP_CLASS[zone] : 'border-grey-5 text-character-secondary'
							}`}
						>
							{ZONE_DISPLAY[zone]}
						</button>
					))}
				</div>
				<div>
					<div className="bodymedium mb-1 text-character-primary">
						Reason <span className="text-red-500">*</span>
					</div>
					<Input.TextArea
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						placeholder="Why are you overriding the replacement index?"
						autoSize={{ minRows: 3, maxRows: 8 }}
					/>
				</div>
			</div>
		</Modal>
	);
};

// -- Main card ---------------------------------------------------------------

const ReplacementIndexCard: FC<ReplacementIndexCardProps> = ({ asset, userType }) => {
	const assetId = nullSafeGet('id', asset);
	const assetTypeId = nullSafeGetOrElse('assetType.id', asset, null);
	const dispatch = useDispatch();
	const [data, setData] = useState<RepairReplaceScore | null>(null);
	const [loading, setLoading] = useState(true);
	const [overrideModalOpen, setOverrideModalOpen] = useState(false);
	const [overrideInitialZone, setOverrideInitialZone] = useState<ActiveZone>('repair');
	const [deepAnalysisOpen, setDeepAnalysisOpen] = useState(false);
	const [improveFieldKey, setImproveFieldKey] = useState<string | null>(null);

	// Fetch + cancel-on-unmount pair. Returning the fetch promise lets callers
	// (e.g. the Improve-confidence modal) await the refresh after a PATCH.
	const fetchScore = React.useCallback((): Promise<void> => {
		if (!assetId) return Promise.resolve();
		setLoading(true);
		return (dispatch as any)(assetRepairReplaceScoreGet(userType)(assetId))
			.then((result: RepairReplaceScore) => {
				setData(result);
				setLoading(false);
			})
			.catch(() => {
				setData(null);
				setLoading(false);
			});
	}, [assetId, dispatch, userType]);

	useEffect(() => {
		fetchScore();
	}, [fetchScore]);

	if (loading || !data) {
		return (
			<Card>
				<Skeleton active paragraph={{ rows: 4 }} />
			</Card>
		);
	}

	const confidenceLevel: ConfidenceLabel = nullSafeGetOrElse(
		'confidence.label',
		data,
		'insufficient_data'
	);
	const confidenceLabel = confidenceDisplayLabel(confidenceLevel);
	const confidenceValue = nullSafeGet('confidence.value', data);
	const topLevelMissingInputs: string[] = nullSafeGetOrElse('confidence.missingInputs', data, []);
	const improveByLinks = ctaLinksFromMissingInputs(topLevelMissingInputs, {
		assetId,
		assetTypeId,
		userType,
	});

	const isInsufficient = data.recommendation === 'insufficient_data';
	const calculatedZone: ActiveZone | null = isInsufficient
		? null
		: (data.recommendation as ActiveZone);
	const overrideZone: ActiveZone | null =
		data.userOverrideRecommendation && data.userOverrideRecommendation !== 'insufficient_data'
			? (data.userOverrideRecommendation as ActiveZone)
			: null;
	const overrideNote = data.userOverrideRecommendationNotes || null;
	// Primary chip (the interactive dropdown) reflects the override when present,
	// falling back to the calculated value. The calculated value is rendered
	// separately as a grey informational pill when an override is in effect.
	const zoneKey: ActiveZone | null = overrideZone || calculatedZone;
	const isOverridden = !!overrideZone;

	const openOverrideFor = (zone: ActiveZone) => {
		setOverrideInitialZone(zone);
		setOverrideModalOpen(true);
	};

	const headerLeft = (
		<div className="flex items-center gap-xs">
			<span className="h5medium uppercase text-character-primary">Replacement Index</span>
			<Popover content={<div style={{ maxWidth: 280 }}>{HEADER_INFO_COPY}</div>}>
				<InfoCircleOutlined className="text-grey-7" />
			</Popover>
		</div>
	);

	const confidencePopoverContent = (
		<div style={{ maxWidth: 300 }}>
			<div className="bodymedium text-character-primary">
				Overall Confidence:{' '}
				<span className={CONFIDENCE_TEXT_COLOR[confidenceLevel]}>{confidenceLabel}</span>
			</div>
			<div className="bodyregular mt-2 text-character-primary">
				Reflects the quality and completeness of the data used for the index.
			</div>
			{improveByLinks.length > 0 ? (
				<>
					<div className="my-3 border-0 border-t border-solid border-grey-4" />
					<div className="bodymedium text-character-primary">Improve by:</div>
					<CtaLinkList links={improveByLinks} onSelect={setImproveFieldKey} />
				</>
			) : null}
		</div>
	);

	const headerRight = (
		<Button
			type="ghost"
			icon={<AIIcon className="mr-2" />}
			onClick={() => setDeepAnalysisOpen(true)}
		>
			Deep Analysis
		</Button>
	);

	const deepAnalysisModal = (
		<DeepAnalysisModal
			visible={deepAnalysisOpen}
			onClose={() => setDeepAnalysisOpen(false)}
			assetId={assetId}
		/>
	);

	if (isInsufficient) {
		return (
			<>
				<Card title={headerLeft} extra={headerRight}>
					<div className="bodymedium text-character-primary">
						Insufficient data to provide this information yet.
					</div>
					<Row className="mt-4 items-stretch" gutter={16}>
						<Col xs={24} md={12}>
							<CostsTile
								dim={data.dimensions.costPressure}
								asset={asset}
								assetId={assetId}
								userType={userType}
								onCtaSelect={setImproveFieldKey}
							/>
						</Col>
						<Col xs={24} md={12}>
							<UsefulLifeTile
								dim={data.dimensions.remainingLife}
								asset={asset}
								assetId={assetId}
								userType={userType}
								onCtaSelect={setImproveFieldKey}
							/>
						</Col>
					</Row>
				</Card>
				<ImproveConfidenceModal
					fieldKey={improveFieldKey}
					assetId={assetId}
					userType={userType}
					onClose={() => setImproveFieldKey(null)}
					onSaved={fetchScore}
				/>
				{deepAnalysisModal}
			</>
		);
	}

	return (
		<>
			<Card title={headerLeft} extra={headerRight}>
				<ZoneAndUrgencyRow
					zone={zoneKey as ActiveZone}
					calculatedZone={(calculatedZone || zoneKey) as ActiveZone}
					overrideZone={overrideZone}
					overrideNote={overrideNote}
					urgency={data.urgency}
					recommendation={data.recommendation}
					confidence={confidenceLevel}
					assetId={assetId}
					userType={userType}
					onZoneSelect={openOverrideFor}
				/>

				<Popover content={confidencePopoverContent} placement="top">
					<div>
						<ZoneProgressBar
							markerPosition={data.markerPosition}
							bands={data.bands}
							confidence={confidenceLevel}
							muted={isOverridden}
						/>
					</div>
				</Popover>

				<Row className="mt-4 items-stretch" gutter={16}>
					<Col xs={24} md={8}>
						<CostsTile
							dim={data.dimensions.costPressure}
							asset={asset}
							assetId={assetId}
							userType={userType}
							onCtaSelect={setImproveFieldKey}
						/>
					</Col>
					<Col xs={24} md={8}>
						<ReliabilityTile dim={data.dimensions.downtimeImportance} />
					</Col>
					<Col xs={24} md={8}>
						<UsefulLifeTile
							dim={data.dimensions.remainingLife}
							asset={asset}
							assetId={assetId}
							userType={userType}
							onCtaSelect={setImproveFieldKey}
						/>
					</Col>
				</Row>
			</Card>
			{zoneKey ? (
				<ChangeReplacementIndexModal
					open={overrideModalOpen}
					initialZone={overrideInitialZone}
					assetId={assetId}
					userType={userType}
					onCancel={() => setOverrideModalOpen(false)}
					onSaved={fetchScore}
				/>
			) : null}
			<ImproveConfidenceModal
				fieldKey={improveFieldKey}
				assetId={assetId}
				userType={userType}
				onClose={() => setImproveFieldKey(null)}
				onSaved={fetchScore}
			/>
			{deepAnalysisModal}
		</>
	);
};

export default withUserType(ReplacementIndexCard);
